// src/components/communication/TeamsConfigPanel.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Users,
  Plus,
  Edit2,
  Trash2,
  Shield,
  Check,
  X,
  Sparkles,
  UserCheck,
  Wrench,
  Layers,
  AlertCircle,
} from "lucide-react";
import { pb } from "../../services/pocketbase";
import { colaboradoresService } from "../../services/colaboradoresService";

const DEFAULT_TEAMS_PRESET = [
  {
    nome: "Oficina & Fundição",
    descricao: "Produção bruta, impressão 3D, injeção de cera e fundição.",
  },
  {
    nome: "Cravação",
    descricao: "Cravação de pedras naturais e zircônias.",
  },
  {
    nome: "Acabamento & Polimento",
    descricao: "Lixamento, polimento manual/mecânico, resina e acabamento final.",
  },
  {
    nome: "Logística & Expedição",
    descricao: "Conferência, embalagem de pedidos, emissão de NF-e e envio Correios.",
  },
  {
    nome: "Comercial & Atendimento",
    descricao: "Vendas, SAC, atendimento a revendedoras e suporte a clientes.",
  },
  {
    nome: "Design & Modelagem CAD",
    descricao: "Modelagem 3D, matrizes, desenvolvimento de novos produtos e prototipagem.",
  },
];

export default function TeamsConfigPanel() {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(false);

  // Form states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [lider, setLider] = useState("");
  const [viceLider, setViceLider] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Carrega todos os dados necessários
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [equipesList, usersList, configsList, opsList] = await Promise.all([
        colaboradoresService.fetchEquipes().catch(() => []),
        pb.collection("users").getFullList({ sort: "name" }).catch(() => []),
        pb.collection("colaboradores_config").getFullList().catch(() => []),
        pb.collection("oficina_operadores").getFullList({ filter: "ativo = true" }).catch(() => []),
      ]);

      setTeams(equipesList || []);
      setUsers(usersList || []);
      setConfigs(configsList || []);
      setOperators(opsList || []);
    } catch (err) {
      console.error("Erro ao carregar dados de equipes:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Abre modal para nova equipe
  const handleOpenNew = () => {
    setEditingId(null);
    setNome("");
    setDescricao("");
    setLider("");
    setViceLider("");
    setSelectedUserIds(new Set());
    setError(null);
    setIsModalOpen(true);
  };

  // Abre modal para edição
  const handleEdit = (team) => {
    setEditingId(team.id);
    setNome(team.nome || "");
    setDescricao(team.descricao || "");
    setLider(team.lider || "");
    setViceLider(team.vice_lider || "");

    // Pega usuários associados a este time (campo membros direto + legado colaboradores_config)
    const memberIds = new Set();
    if (Array.isArray(team.membros)) {
      team.membros.forEach((m) => {
        const mId = typeof m === "object" ? m.id : m;
        if (mId) memberIds.add(mId);
      });
    }
    configs.forEach((c) => {
      if (c.equipe === team.id && c.colaborador) {
        memberIds.add(c.colaborador);
      }
    });
    setSelectedUserIds(memberIds);
    setError(null);
    setIsModalOpen(true);
  };

  // Salvar equipe e atualizar vínculos dos usuários
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nome.trim()) {
      setError("O nome da equipe é obrigatório.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        nome: nome.trim(),
        descricao: descricao.trim(),
        lider: lider || null,
        vice_lider: viceLider || null,
        membros: Array.from(selectedUserIds),
      };

      const savedTeam = await colaboradoresService.saveEquipe(editingId, payload);
      const teamId = savedTeam.id;

      // Para compatibilidade, garante que colaboradores_config tenha uma equipe primária se estiver vazio
      for (const uId of selectedUserIds) {
        const existingConfig = configs.find((c) => c.colaborador === uId);
        if (existingConfig && !existingConfig.equipe) {
          await pb.collection("colaboradores_config").update(existingConfig.id, { equipe: teamId }).catch(() => {});
        } else if (!existingConfig) {
          await pb.collection("colaboradores_config").create({
            colaborador: uId,
            equipe: teamId,
            ativo: true,
          }).catch(() => {});
        }
      }

      setIsModalOpen(false);
      await loadData();
    } catch (err) {
      console.error("Erro ao salvar equipe:", err);
      setError("Falha ao salvar equipe: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Excluir equipe
  const handleDelete = async (teamId) => {
    if (!window.confirm("Deseja realmente excluir esta equipe? Os colaboradores vinculados a ela ficarão sem equipe.")) {
      return;
    }

    try {
      await colaboradoresService.deleteEquipe(teamId);

      // Limpa a equipe dos colaboradores_config correspondentes
      const relatedConfigs = configs.filter((c) => c.equipe === teamId);
      for (const c of relatedConfigs) {
        await pb.collection("colaboradores_config").update(c.id, { equipe: null }).catch(() => {});
      }

      await loadData();
    } catch (err) {
      alert("Erro ao excluir equipe: " + err.message);
    }
  };

  // Inicializar equipes operacionais padrão com 1 clique
  const handleSeedDefaultTeams = async () => {
    if (!window.confirm("Deseja cadastrar as 6 equipes operacionais padrão da Sempre Joias?")) return;

    setLoading(true);
    try {
      for (const teamPreset of DEFAULT_TEAMS_PRESET) {
        await colaboradoresService.saveEquipe(null, teamPreset);
      }
      await loadData();
    } catch (err) {
      alert("Erro ao cadastrar equipes padrão: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      {/* HEADER DO PAINEL */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Users className="text-purple-600" size={22} />
            Gestão de Equipes & Times
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Cadastre os setores operacionais da empresa e defina quais colaboradores pertencem a cada time para atribuição de tarefas no chat.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {teams.length === 0 && (
            <button
              onClick={handleSeedDefaultTeams}
              className="bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition cursor-pointer"
              title="Cadastrar equipes operacionais pré-definidas"
            >
              <Sparkles size={16} />
              <span>Inicializar Equipes Padrão</span>
            </button>
          )}

          <button
            onClick={handleOpenNew}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm shadow-purple-600/30 transition cursor-pointer"
          >
            <Plus size={16} />
            <span>Nova Equipe</span>
          </button>
        </div>
      </div>

      {/* CARD CASO NÃO HAJA EQUIPES */}
      {teams.length === 0 && !loading && (
        <div className="p-8 rounded-2xl bg-purple-50/50 border border-purple-200 text-center flex flex-col items-center justify-center space-y-3">
          <div className="w-14 h-14 rounded-2xl bg-purple-100 text-purple-600 flex items-center justify-center">
            <Users size={28} />
          </div>
          <h4 className="font-bold text-base text-slate-800">Nenhuma equipe cadastrada ainda</h4>
          <p className="text-xs text-slate-600 max-w-md">
            Clique no botão abaixo para gerar instantaneamente as equipes operacionais sugeridas (Cravação, Oficina, Acabamento, Logística, etc.) ou cadastre suas próprias.
          </p>
          <button
            onClick={handleSeedDefaultTeams}
            className="bg-purple-600 hover:bg-purple-700 text-white px-5 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 shadow-md transition cursor-pointer"
          >
            <Sparkles size={16} />
            <span>Criar Equipes Operacionais Padrão</span>
          </button>
        </div>
      )}

      {/* LISTA DE EQUIPES */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {teams.map((team) => {
          // Membros usuários desta equipe (membros diretos + legado colaboradores_config)
          const rawMembros = Array.isArray(team.membros)
            ? team.membros.map((m) => (typeof m === "object" ? m.id : m))
            : [];
          const teamMemberConfigs = configs.filter((c) => c.equipe === team.id);
          const legacyUserIds = teamMemberConfigs.map((c) => c.colaborador);

          const allMemberUserIds = new Set([...rawMembros, ...legacyUserIds]);
          const memberUsers = users.filter((u) => allMemberUserIds.has(u.id));

          // Operadores associados aos usuários deste time
          const memberUserIds = new Set(memberUsers.map((u) => u.id));
          const linkedOperators = operators.filter(
            (op) => op.colaborador && memberUserIds.has(op.colaborador)
          );

          const leaderUser = users.find((u) => u.id === team.lider);
          const viceLeaderUser = users.find((u) => u.id === team.vice_lider);

          return (
            <div
              key={team.id}
              className="bg-white p-5 rounded-2xl border border-slate-200/90 shadow-xs hover:shadow-sm transition flex flex-col justify-between space-y-4"
            >
              <div className="space-y-3">
                {/* TOPO DO CARD */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center font-bold">
                      <Users size={18} />
                    </div>
                    <div>
                      <h4 className="font-bold text-sm text-slate-800 flex items-center gap-2">
                        {team.nome}
                        <span className="bg-purple-50 text-purple-700 text-[10px] font-bold px-2 py-0.5 rounded-full border border-purple-200">
                          {memberUsers.length} colaboradores
                        </span>
                      </h4>
                      {team.descricao && (
                        <p className="text-xs text-slate-500 line-clamp-1">{team.descricao}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => handleEdit(team)}
                      className="p-1.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition cursor-pointer"
                      title="Editar Equipe"
                    >
                      <Edit2 size={15} />
                    </button>
                    <button
                      onClick={() => handleDelete(team.id)}
                      className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition cursor-pointer"
                      title="Excluir Equipe"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>

                {/* LÍDER E VICE-LÍDER */}
                {(leaderUser || viceLeaderUser) && (
                  <div className="flex flex-wrap gap-2 text-[11px] pt-2 border-t border-slate-100">
                    {leaderUser && (
                      <span className="flex items-center gap-1 font-semibold text-slate-700 bg-amber-50 border border-amber-200/80 px-2 py-0.5 rounded-md">
                        <Shield size={11} className="text-amber-600" /> Líder: {leaderUser.name || leaderUser.username}
                      </span>
                    )}
                    {viceLeaderUser && (
                      <span className="flex items-center gap-1 font-semibold text-slate-700 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-md">
                        <UserCheck size={11} className="text-slate-500" /> Vice: {viceLeaderUser.name || viceLeaderUser.username}
                      </span>
                    )}
                  </div>
                )}

                {/* CHIPS DE MEMBROS */}
                <div className="space-y-1.5 pt-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                    Integrantes da Equipe:
                  </span>
                  <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto custom-scrollbar">
                    {memberUsers.map((u) => (
                      <span
                        key={u.id}
                        className="bg-slate-50 hover:bg-purple-50 border border-slate-200 text-slate-700 px-2 py-0.5 rounded-md text-[11px] font-medium transition flex items-center gap-1"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                        {u.name || u.username}
                      </span>
                    ))}
                    {linkedOperators.map((op) => (
                      <span
                        key={op.id}
                        className="bg-teal-50 border border-teal-200 text-teal-800 px-2 py-0.5 rounded-md text-[11px] font-medium flex items-center gap-1"
                      >
                        <Wrench size={10} className="text-teal-600" />
                        {op.nome} (Bancada)
                      </span>
                    ))}
                    {memberUsers.length === 0 && (
                      <span className="text-xs text-slate-400 italic">
                        Nenhum colaborador atribuído a esta equipe ainda.
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* MODAL DE CRIAÇÃO / EDIÇÃO */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh] animate-scaleUp"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 bg-slate-900 text-white shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 border border-purple-400/30 flex items-center justify-center text-purple-400">
                  <Users size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-base text-white">
                    {editingId ? "Editar Equipe" : "Nova Equipe"}
                  </h3>
                  <p className="text-xs text-slate-400">
                    Defina nome, liderança e os membros participantes
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
              {error && (
                <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 flex items-center gap-2 text-xs">
                  <AlertCircle size={16} className="shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* NOME DA EQUIPE */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Nome da Equipe *
                </label>
                <input
                  type="text"
                  required
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Ex: Cravação"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 shadow-2xs"
                />
              </div>

              {/* DESCRIÇÃO */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Descrição / Função da Equipe
                </label>
                <input
                  type="text"
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  placeholder="Ex: Responsável pela cravação de pedras, garras e acabamento fino"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 shadow-2xs"
                />
              </div>

              {/* LÍDER E VICE-LÍDER */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                    <Shield size={12} className="text-amber-500" />
                    Líder (Opcional)
                  </label>
                  <select
                    value={lider}
                    onChange={(e) => setLider(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-medium bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Nenhum líder definido</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name || u.username}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                    <UserCheck size={12} className="text-slate-500" />
                    Vice-Líder (Opcional)
                  </label>
                  <select
                    value={viceLider}
                    onChange={(e) => setViceLider(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-medium bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Nenhum vice-líder</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name || u.username}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* SELEÇÃO DE MEMBROS DA EQUIPE */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Colaboradores Participantes ({selectedUserIds.size} selecionados)
                  </label>
                  <span className="text-[11px] text-purple-600 font-semibold">
                    Clique para incluir ou remover
                  </span>
                </div>

                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 max-h-52 overflow-y-auto space-y-1.5 custom-scrollbar">
                  {users.map((u) => {
                    const isSelected = selectedUserIds.has(u.id);
                    return (
                      <div
                        key={u.id}
                        onClick={() => toggleUserSelection(u.id)}
                        className={`p-2 rounded-lg border text-xs flex items-center justify-between transition cursor-pointer select-none ${
                          isSelected
                            ? "bg-purple-50 border-purple-300 text-purple-900 font-bold"
                            : "bg-white border-slate-200 text-slate-600 hover:bg-slate-100"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span
                            className={`w-4 h-4 rounded flex items-center justify-center border transition ${
                              isSelected
                                ? "bg-purple-600 border-purple-600 text-white"
                                : "border-slate-300"
                            }`}
                          >
                            {isSelected && <Check size={12} strokeWidth={3} />}
                          </span>
                          <span className="font-medium">{u.name || u.username}</span>
                          {operators
                            .filter((op) => op.colaborador === u.id)
                            .map((op) => (
                              <span
                                key={op.id}
                                className="text-[10px] font-bold text-teal-700 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded flex items-center gap-1 shrink-0"
                              >
                                <Wrench size={9} /> {op.nome} (Bancada)
                              </span>
                            ))}
                        </span>
                        <span className="text-[10px] text-slate-400 font-normal shrink-0">
                          {u.role || "user"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* AÇÕES */}
              <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold shadow-sm shadow-purple-600/30 transition disabled:opacity-50 cursor-pointer flex items-center gap-1.5"
                >
                  {saving ? "Salvando..." : "Salvar Equipe"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
